import { useEffect, useState } from "react";

import { useSelector } from "react-redux";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import { useStatsigContext } from "../../elements-statsig-provider/utils";
import { hasDiyListing } from "../utils/host-hub-marketing-banners.utils";

import useHostHubOwnerListings from "./useHostHubOwnerListings";

type LoadedDismissal = {
  isDismissed: boolean;
  storageKey: string;
};

const readIsDismissed = (storageKey: string) => {
  try {
    return window.localStorage.getItem(storageKey) === "true";
  } catch {
    return false;
  }
};

const writeIsDismissed = (storageKey: string) => {
  try {
    window.localStorage.setItem(storageKey, "true");
    return true;
  } catch {
    return false;
  }
};

/**
 * Controls visibility of the date based pricing banner on the Host Hub.
 * Shown to hosts with at least one activated DIY listing while the
 * `date_based_pricing` and `date_based_pricing_callout` gates are on, until
 * they dismiss it. There's no expiration: turning off
 * `date_based_pricing_callout` retires it.
 */
const useDateBasedPricingBanner = () => {
  const ssoId = useSelector(selectProfileSSOid);
  const storageKey = ssoId
    ? `hostHub:dateBasedPricing:dismissed:${ssoId}`
    : null;
  const { featureGates, initialized } = useStatsigContext();
  const { isLoading: isListingsLoading, listings } = useHostHubOwnerListings();
  const gates = featureGates as {
    date_based_pricing?: boolean;
    date_based_pricing_callout?: boolean;
  };
  const isGateOn =
    (gates?.date_based_pricing ?? false) &&
    (gates?.date_based_pricing_callout ?? false);

  const [loadedDismissal, setLoadedDismissal] = useState<LoadedDismissal>();
  const isDismissed =
    storageKey && loadedDismissal?.storageKey === storageKey
      ? loadedDismissal.isDismissed
      : undefined;

  useEffect(() => {
    if (!storageKey) return;
    setLoadedDismissal({
      isDismissed: readIsDismissed(storageKey),
      storageKey,
    });
  }, [storageKey]);

  const dismiss = () => {
    if (!storageKey) return;
    writeIsDismissed(storageKey);
    setLoadedDismissal({ isDismissed: true, storageKey });
  };

  const isVisible =
    initialized &&
    !isListingsLoading &&
    isGateOn &&
    isDismissed === false &&
    hasDiyListing(listings);

  return { dismiss, isVisible };
};

export default useDateBasedPricingBanner;
